import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IResearchProductivityRequest,
  ResearchStageEnum,
  ResearchWorkTypeEnum,
} from "./types";

export enum ProgressStatusFilterEnum {
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  ALL = "ALL",
}

export interface IResearchFilterState {
  common: {
    search: string;
    clientCompanyId: string;
    workType?: ResearchWorkTypeEnum;
  };
  dashboard: {
    assignedUserId: string;
    priorParticipantUserId: string;
    activityFrom: string;
    activityTo: string;
    availableToMe: boolean;
    overdueOnly: boolean;
    archivedOnly: boolean;
    page: number;
    stage?: ResearchStageEnum;
  };
  clientProgress: {
    status: ProgressStatusFilterEnum;
  };
  productivity: IResearchProductivityRequest;
}

export const initialResearchFilterState: IResearchFilterState = {
  common: {
    search: "",
    clientCompanyId: "",
    workType: undefined,
  },
  dashboard: {
    stage: undefined,
    assignedUserId: "",
    priorParticipantUserId: "",
    activityFrom: "",
    activityTo: "",
    availableToMe: false,
    overdueOnly: false,
    archivedOnly: false,
    page: 0,
  },
  clientProgress: {
    status: ProgressStatusFilterEnum.ACTIVE,
  },
  productivity: {},
};

const resetDashboardPage = (state: IResearchFilterState) => {
  state.dashboard.page = 0;
};

const researchFiltersSlice = createSlice({
  name: "researchFilters",
  initialState: initialResearchFilterState,
  reducers: {
    resetResearchDashboardFilters: (state) => {
      state.common = initialResearchFilterState.common;
      state.dashboard = initialResearchFilterState.dashboard;
    },
    resetResearchClientProgressFilters: (state) => {
      state.common = initialResearchFilterState.common;
      state.clientProgress = initialResearchFilterState.clientProgress;
      resetDashboardPage(state);
    },
    setResearchSearch: (state, { payload }: PayloadAction<string>) => {
      state.common.search = payload;
      resetDashboardPage(state);
    },
    setResearchClientCompanyId: (state, { payload }: PayloadAction<string>) => {
      state.common.clientCompanyId = payload;
      resetDashboardPage(state);
    },
    setResearchWorkType: (
      state,
      { payload }: PayloadAction<ResearchWorkTypeEnum | undefined>
    ) => {
      state.common.workType = payload;
      resetDashboardPage(state);
    },
    setResearchDashboardStage: (
      state,
      { payload }: PayloadAction<ResearchStageEnum | undefined>
    ) => {
      state.dashboard.stage = payload;
      resetDashboardPage(state);
    },
    setResearchAssignedUserId: (state, { payload }: PayloadAction<string>) => {
      state.dashboard.assignedUserId = payload;
      resetDashboardPage(state);
    },
    setResearchPriorParticipantUserId: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.dashboard.priorParticipantUserId = payload;
      resetDashboardPage(state);
    },
    setResearchActivityFrom: (state, { payload }: PayloadAction<string>) => {
      state.dashboard.activityFrom = payload;
      resetDashboardPage(state);
    },
    setResearchActivityTo: (state, { payload }: PayloadAction<string>) => {
      state.dashboard.activityTo = payload;
      resetDashboardPage(state);
    },
    setResearchAvailableToMe: (state, { payload }: PayloadAction<boolean>) => {
      state.dashboard.availableToMe = payload;
      resetDashboardPage(state);
    },
    setResearchOverdueOnly: (state, { payload }: PayloadAction<boolean>) => {
      state.dashboard.overdueOnly = payload;
      resetDashboardPage(state);
    },
    setResearchArchivedOnly: (state, { payload }: PayloadAction<boolean>) => {
      state.dashboard.archivedOnly = payload;
      if (payload) {
        state.dashboard.availableToMe = false;
        state.dashboard.overdueOnly = false;
      }
      resetDashboardPage(state);
    },
    setResearchDashboardPage: (state, { payload }: PayloadAction<number>) => {
      state.dashboard.page = payload;
    },
    setResearchClientProgressStatus: (
      state,
      { payload }: PayloadAction<ProgressStatusFilterEnum>
    ) => {
      state.clientProgress.status = payload;
    },
    setResearchProductivityRange: (
      state,
      { payload }: PayloadAction<IResearchProductivityRequest>
    ) => {
      state.productivity = payload;
    },
  },
});

export const {
  resetResearchDashboardFilters,
  resetResearchClientProgressFilters,
  setResearchActivityFrom,
  setResearchActivityTo,
  setResearchArchivedOnly,
  setResearchAssignedUserId,
  setResearchAvailableToMe,
  setResearchClientCompanyId,
  setResearchClientProgressStatus,
  setResearchDashboardPage,
  setResearchDashboardStage,
  setResearchOverdueOnly,
  setResearchPriorParticipantUserId,
  setResearchProductivityRange,
  setResearchSearch,
  setResearchWorkType,
} = researchFiltersSlice.actions;

export default researchFiltersSlice;
